/**
 * localStorage that cannot throw.
 *
 * Every access is guarded: Safari private mode throws on setItem, embedded
 * webviews and strict cookie settings throw on merely READING localStorage,
 * and a tracking SDK that throws inside a customer's page breaks that page.
 * A visitor with no storage simply gets a per-pageload session instead.
 */

let memoryFallback: Record<string, string> = {};
let storageWorks: boolean | null = null;

const probe = (): boolean => {
  if (storageWorks !== null) {
    return storageWorks;
  }
  try {
    const key = "__snipcat_probe__";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    storageWorks = true;
  } catch {
    storageWorks = false;
  }
  return storageWorks;
};

export const readStored = (key: string): string | null => {
  if (!probe()) {
    return memoryFallback[key] ?? null;
  }
  try {
    return window.localStorage.getItem(key) || null;
  } catch {
    return memoryFallback[key] ?? null;
  }
};

export const writeStored = (key: string, value: string | null): void => {
  if (value === null) {
    delete memoryFallback[key];
  } else {
    memoryFallback[key] = value;
  }
  if (!probe()) {
    return;
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // quota or a private-mode refusal - the memory copy above is the answer
  }
};

export const readStoredJson = <T>(key: string, fallback: T): T => {
  const raw = readStored(key);
  if (!raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

export const writeStoredJson = (key: string, value: unknown): void => {
  try {
    writeStored(key, JSON.stringify(value));
  } catch {
    // circular structure - not worth breaking the page over
  }
};

/** Test seam: forget the probe result and the in-memory copy. */
export const resetStorageState = (): void => {
  storageWorks = null;
  memoryFallback = {};
};
